/**
 * Scrolling band of logos — used for customer logos on the home page and for
 * the manufacturers Athena represents.
 *
 * Logos arrive in wildly different shapes: tall squares, wide wordmarks, small
 * icons. Each is placed in a fixed box and scaled to fit inside it, so every
 * logo occupies the same footprint and sits centred regardless of aspect ratio.
 * Without that, a square logo towers over a wide one and the row looks
 * accidental.
 *
 * With only a few logos the row is centred and static: a marquee of two items
 * scrolling past looks broken rather than deliberate. It starts scrolling once
 * there are enough to fill the width.
 */

import { useState } from "react";

const SCROLL_THRESHOLD = 5;

function Logo({ item }) {
  const [failed, setFailed] = useState(false);
  const hasLogo = item.logo && !failed;

  // A partner without an uploaded logo falls back to its name rather than
  // dropping out of the row — the same treatment the Our Partners page gives
  // it, so a half-populated list still reads as one deliberate band.
  const inner = hasLogo ? (
    <img
      src={item.logo}
      alt={item.name}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className="max-h-full max-w-full object-contain"
    />
  ) : (
    <span className="text-xl md:text-2xl font-extrabold tracking-tight text-ink/70 whitespace-nowrap">
      {item.name}
    </span>
  );

  return (
    <span className="inline-flex items-center justify-center shrink-0 h-16 md:h-20 w-32 md:w-44 px-4">
      {item.website ? (
        <a
          href={item.website}
          target="_blank"
          rel="noopener noreferrer"
          title={item.name}
          className="flex items-center justify-center w-full h-full"
        >
          {inner}
        </a>
      ) : (
        inner
      )}
    </span>
  );
}

export default function LogoMarquee({ items = [], className = "" }) {
  if (!items.length) return null;

  const key = (item, prefix = "") => `${prefix}${item.id || item.slug || item.name}`;

  // Not enough to scroll convincingly — centre them instead.
  if (items.length < SCROLL_THRESHOLD) {
    return (
      <div className={`flex flex-wrap items-center justify-center gap-y-4 ${className}`}>
        {items.map((item) => (
          <Logo key={key(item)} item={item} />
        ))}
      </div>
    );
  }

  return (
    <div className={`marquee-pause overflow-hidden ${className}`}>
      <div className="inline-flex items-center animate-marquee whitespace-nowrap">
        {items.map((item) => (
          <Logo key={key(item)} item={item} />
        ))}
        {/* Duplicated for a seamless loop, hidden from screen readers so each
            name is announced once. */}
        <span className="inline-flex items-center" aria-hidden="true">
          {items.map((item) => (
            <Logo key={key(item, "dup-")} item={item} />
          ))}
        </span>
      </div>
    </div>
  );
}
